import { useState, useMemo } from 'react'
import {
  Modal,
  Input,
  Button,
  Typography,
  Flex,
  Space,
  Tag,
  Divider,
  Radio,
  Alert,
} from 'antd'
import { CloseCircleOutlined, SendOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useStore } from 'effector-react'

import { submitReject, rejectTransactionRequest } from './model'
import {
  depositRejectionReason,
  transactionRequestDestinationType,
} from '../../../const'
import { DepositEdit } from '../../edit'

const { Text } = Typography

type Props = {
  request: DepositEdit
  className?: string
  disabled?: boolean
}

type ReasonDef = {
  id: string
  key: string
  label: string
  fields: { key: string; label: string }[]
}

function parseFields(label: string): { key: string; label: string }[] {
  const fields: { key: string; label: string }[] = []
  const regex = /\{(.*?)\}/g
  let match: RegExpExecArray | null
  while ((match = regex.exec(label)) !== null) {
    fields.push({
      key: match[1],
      label: match[1].replace(/_/g, ' '),
    })
  }
  return fields
}

function buildPreview(
  label: string,
  values: Record<string, string>,
): string {
  return label.replace(
    /\{(.*?)\}/g,
    (_, key) => values[key] || `[${key.replace(/_/g, ' ')}]`,
  )
}

const REASONS: ReasonDef[] = Object.entries(depositRejectionReason).map(
  ([name, fn]) => {
    const config = fn()
    return {
      id: name,
      key: config.key,
      label: config.label,
      fields: parseFields(config.label),
    }
  },
)

function MetaItem({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  if (value === null || value === undefined || value === '') return null
  return (
    <Flex align="baseline" gap={4}>
      <Text
        type="secondary"
        style={{ fontSize: 11, whiteSpace: 'nowrap' }}
      >
        {label}:
      </Text>
      <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>
        {typeof value === 'boolean'
          ? value
            ? 'Yes'
            : 'No'
          : value}
      </Text>
    </Flex>
  )
}

function ReasonCard({
  reason,
  selected,
  onSelect,
  values,
  onChange,
}: {
  reason: ReasonDef
  selected: string | null
  onSelect: (id: string) => void
  values: Record<string, string>
  onChange: (key: string, val: string) => void
}) {
  const isSelected = selected === reason.id
  const border = isSelected ? '1.5px solid var(--crm-accent-blue, #3b82f6)' : '1px solid var(--crm-border)'
  const bg = isSelected ? 'var(--crm-bg-elevated)' : 'var(--crm-bg)'
  const preview = isSelected ? buildPreview(reason.label, values) : null

  return (
    <div
      onClick={() => onSelect(reason.id)}
      style={{
        border,
        borderRadius: 8,
        padding: '12px 14px',
        background: bg,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      <Flex align="flex-start" gap={10}>
        <Radio
          checked={isSelected}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 13 }}>
            {reason.label.replace(/\{.*?\}/g, '___')}
          </Text>

          {isSelected && reason.fields.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {reason.fields.map((f) => (
                <div key={f.key} style={{ marginBottom: 8 }}>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 11,
                      display: 'block',
                      marginBottom: 3,
                    }}
                  >
                    {f.label}
                  </Text>
                  <Input
                    size="small"
                    placeholder={f.label}
                    value={values[f.key] || ''}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus={f.key === reason.fields[0].key}
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
              ))}
            </div>
          )}

          {isSelected && (
            <div
              style={{
                marginTop: 10,
                background: 'var(--crm-bg-elevated)',
                border: '1px solid var(--crm-border)',
                borderRadius: 6,
                padding: '8px 10px',
              }}
            >
              <Flex
                align="center"
                gap={6}
                style={{ marginBottom: 4 }}
              >
                <SendOutlined
                  style={{ color: 'var(--crm-accent-blue, #3b82f6)', fontSize: 11 }}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Message to client
                </Text>
              </Flex>
              <Text style={{ fontSize: 12, fontStyle: 'italic' }}>
                "{preview}"
              </Text>
            </div>
          )}
        </div>
      </Flex>
    </div>
  )
}

export const RejectModal = ({
  request,
  className,
  disabled,
}: Props) => {
  const isLoading = useStore(rejectTransactionRequest.pending)
  const [isVisible, setVisible] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [fieldVals, setFieldVals] = useState<Record<string, string>>({})
  const [error, setError] = useState('')

  const handleSelect = (id: string) => {
    setSelected(id)
    setFieldVals({})
    setError('')
  }

  const handleField = (key: string, val: string) => {
    setFieldVals((prev) => ({ ...prev, [key]: val }))
  }

  const handleClose = () => {
    setVisible(false)
    setSelected(null)
    setFieldVals({})
    setError('')
  }

  const selectedReason = useMemo(
    () => REASONS.find((r) => r.id === selected),
    [selected],
  )

  const canSubmit = useMemo(() => {
    if (!selectedReason) return false
    return selectedReason.fields.every((f) => fieldVals[f.key]?.trim())
  }, [selectedReason, fieldVals])

  const handleSubmit = () => {
    if (!selectedReason) {
      setError('Please select a reason')
      return
    }
    if (!canSubmit) return

    const params: Record<string, string> = {}
    selectedReason.fields.forEach((f) => {
      params[`{${f.key}}`] = fieldVals[f.key] || ''
    })

    submitReject({
      requestId: request.transactionId,
      rejectReason: JSON.stringify({ key: selectedReason.key, params }),
      comment: '',
    })

    handleClose()
  }

  const purseLabel =
    request.destinationType ===
    transactionRequestDestinationType.wallet()
      ? 'Wallet ID'
      : 'Account ID'

  // Filter reasons: novaReject only for nova product, thirdParty excluded unless needed
  const visibleReasons = useMemo(() => {
    return REASONS.filter((r) => {
      if (r.id === 'novaReject') return request.product === 'nova'
      if (r.id === 'thirdParty') return false
      return true
    })
  }, [request.product])

  return (
    <>
      <Button
        type="primary"
        danger
        className={className}
        onClick={() => setVisible(true)}
        disabled={disabled}
      >
        Reject
      </Button>
      <Modal
        open={isVisible}
        onCancel={handleClose}
        title={
          <Flex align="center" gap={8}>
            <CloseCircleOutlined style={{ color: 'var(--crm-accent-red, #ef4444)' }} />
            <span>Set rejection reason</span>
          </Flex>
        }
        width={900}
        footer={
          <Flex justify="space-between" align="center">
            <Text type="secondary" style={{ fontSize: 11 }}>
              {selected
                ? 'Review the message preview before sending'
                : 'Select a reason to continue'}
            </Text>
            <Space>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="primary"
                danger
                disabled={!canSubmit || isLoading}
                loading={isLoading}
                icon={<SendOutlined />}
                onClick={handleSubmit}
              >
                Send & Reject
              </Button>
            </Space>
          </Flex>
        }
        styles={{
          body: {
            padding: '16px 24px',
            maxHeight: '70vh',
            overflowY: 'auto',
          },
          header: { paddingBottom: 12 },
        }}
      >
        {/* Transaction meta */}
        <div
          style={{
            background: 'var(--crm-bg-elevated)',
            border: '1px solid var(--crm-border)',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
          }}
        >
          <Flex gap={24} style={{ marginBottom: 8 }}>
            <Flex gap={4} align="baseline">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Created
              </Text>
              <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>
                {dayjs(request.createdAt).format('DD MMM YYYY, HH:mm')}
              </Text>
            </Flex>
            <Flex gap={4} align="baseline">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Updated
              </Text>
              <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>
                {dayjs(request.updatedAt).format('DD MMM YYYY, HH:mm')}
              </Text>
            </Flex>
          </Flex>

          <Divider style={{ margin: '8px 0' }} />

          <Flex gap={20} wrap="wrap">
            <MetaItem label="ID" value={request.transactionId} />
            <MetaItem
              label="Client ID"
              value={request.client.clientId}
            />
            <MetaItem label={purseLabel} value={request.destinationId} />
            <MetaItem
              label="Processing type"
              value={
                <Tag
                  color={request.isAuto ? 'green' : 'geekblue'}
                  style={{ fontSize: 11 }}
                >
                  {request.isAuto ? 'Auto' : 'Manual'}
                </Tag>
              }
            />
          </Flex>

          <Divider style={{ margin: '8px 0' }} />
          <Flex gap={16} wrap="wrap">
            <MetaItem
              label="clientPurseId"
              value={request.clientPurseId}
            />
            {request.clientPurseId != 0 && (
              <>
                <MetaItem
                  label="isCard"
                  value={Boolean(
                    request.clientPurse?.isCard,
                  ).toString()}
                />
                <MetaItem
                  label="expiry"
                  value={request.clientPurse?.expiry}
                />
                <MetaItem
                  label="mask"
                  value={request.clientPurse?.mask}
                />
                <MetaItem
                  label="type"
                  value={request.clientPurse?.type}
                />
              </>
            )}
          </Flex>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            style={{ marginBottom: 12 }}
          />
        )}

        <Text
          type="secondary"
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            display: 'block',
            marginBottom: 10,
          }}
        >
          Rejection reason
        </Text>

        <Flex vertical gap={8}>
          {visibleReasons.map((reason) => (
            <ReasonCard
              key={reason.id}
              reason={reason}
              selected={selected}
              onSelect={handleSelect}
              values={fieldVals}
              onChange={handleField}
            />
          ))}
        </Flex>
      </Modal>
    </>
  )
}
